import { useState, useEffect, useRef } from "react"
import { router } from "@inertiajs/react"
import { Search, FileText, X } from "lucide-react"
import { Input } from "@/components/ui/input"

interface SearchResult {
  name: string
  path: string
  highlights?: string[]
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

interface SearchOverlayProps {
  onClose?: () => void
}

export function SearchOverlay({ onClose }: SearchOverlayProps) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    inputRef.current?.focus()

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && onClose) {
        onClose()
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (query.length < 2) {
      setResults([])
      return
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await fetch(`/api/search?query=${encodeURIComponent(query)}`, {
          headers: { "X-CSRF-Token": getCSRFToken() },
        })
        const data = await res.json()
        setResults(data.results || [])
      } catch {
        // ignore
      } finally {
        setLoading(false)
      }
    }, 200)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query])

  function handleSelectResult(path: string) {
    const encodedPath = path.replace(/^\/+/, "").split("/").map(segment => encodeURIComponent(segment)).join("/")
    router.visit(`/files/${encodedPath}`)
    onClose?.()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) onClose()
      }}
      data-testid="search-overlay"
    >
      <div className="w-full max-w-lg px-4">
        <div className="w-full rounded-lg border bg-popover shadow-xl overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 border-b px-4 py-3">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search files..."
              className="border-0 shadow-none focus-visible:ring-0 h-auto py-0 px-0"
              data-testid="search-input"
            />
            {query ? (
              <button onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            ) : (
              <kbd className="hidden sm:inline-flex items-center gap-1 rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                /
              </kbd>
            )}
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto p-2">
            {loading && (
              <p className="p-4 text-center text-sm text-muted-foreground">Searching...</p>
            )}

            {!loading && query.length >= 2 && results.length === 0 && (
              <p className="p-4 text-center text-sm text-muted-foreground" data-testid="no-results">
                No results found
              </p>
            )}

            {!loading && query.length < 2 && results.length === 0 && (
              <p className="p-4 text-center text-sm text-muted-foreground">
                Type to search files...
              </p>
            )}

            {results.map((result) => (
              <button
                key={result.path}
                onClick={() => handleSelectResult(result.path)}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left hover:bg-accent"
                data-testid={`search-result-${result.name}`}
              >
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium">{result.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{result.path}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Footer */}
          {results.length > 0 && (
            <div className="border-t px-4 py-2 text-xs text-muted-foreground">
              {results.length} result{results.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
